import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@knack/asterisk-react';
import { z } from 'zod';

import {
  type SearchView,
  type SearchViewFilterRule,
  type SearchViewFilters
} from '@/types/schema/views/SearchView';
import { useCriteriaHelpers } from '@/hooks/helpers/useCriteriaHelpers';
import { isPageEditor } from '@/utils/iframe';
import { useViewContext } from '@/components/views/ViewContext';
import { SearchInputsSortableWrapper } from './search-inputs-sortable/SearchInputsSortableWrapper';
import { SearchInputs } from './SearchInputs';
import { useSearchViewFilters } from './useSearchViewFilters';

export function SearchFilters({
  setShouldShowResults
}: {
  setShouldShowResults: (showResults: boolean) => void;
}) {
  const [t] = useTranslation();

  const { view, sourceTable } = useViewContext<SearchView>();
  const {
    searchViewFilterRules,
    searchViewFieldInputsByFieldKey,
    applySearchViewFilters,
    resetSearchViewFilters,
    getSearchViewFilterRulesFromInputs
  } = useSearchViewFilters();
  const { isCriteriaValueEmpty } = useCriteriaHelpers();

  const filterFormSchema = z.custom<SearchViewFilters>().superRefine((data, context) => {
    // Validate required filter rules
    data.rules.forEach((rule, index) => {
      // If the field is not required, we don't need to validate the value
      if (!searchViewFieldInputsByFieldKey[rule.field].required) {
        return;
      }

      // If the filter is the keyword search, we don't need complex value validation
      if (rule.field === 'keyword_search' && !rule.value) {
        context.addIssue({
          path: ['rules', index, 'value'],
          code: 'custom',
          message: t('errors.value_required')
        });
        return;
      }

      // Otherwise, we need to get the field associated with the filter rule to determine if the value is considered empty or not
      const ruleField = [...sourceTable.fields, ...sourceTable.fieldsFromConnections].find(
        (f) => f.key === rule.field
      );

      if (ruleField && isCriteriaValueEmpty(rule.value, ruleField)) {
        context.addIssue({
          path: ['rules', index, 'value'],
          code: 'custom',
          message: t('errors.value_required')
        });
      }
    });
  });

  const form = useForm<SearchViewFilters>({
    resolver: zodResolver(filterFormSchema),
    defaultValues: { rules: searchViewFilterRules }
  });

  // This effect is necessary to trigger the form validation when the view is mounted, if the "Show results to start" setting is enabled. We need to ensure that the form is valid before performing the search.
  useEffect(() => {
    // If the user is in the page editor, we don't want to trigger the form validation
    if (isPageEditor() || !view.initial_results) {
      return;
    }

    form.trigger().catch(() => {});
  }, [view.initial_results, form]);

  const onSubmit = (newViewFilters: SearchViewFilters) => {
    const eligibleRulesForSearch: SearchViewFilterRule[] = [];

    // We need to keep track of the rules that have been added to the eligibleRulesForSearch array to avoid duplicates
    const addedRuleIds = new Set<string>();

    // Include the rules that have been touched by the user
    const dirtyRuleIndices = Object.keys(form.formState.dirtyFields?.rules || {});
    dirtyRuleIndices.forEach((index) => {
      const ruleIndex = parseInt(index, 10);
      const rule = newViewFilters.rules[ruleIndex];

      if (rule.inputId && !addedRuleIds.has(rule.inputId)) {
        eligibleRulesForSearch.push(rule);
        addedRuleIds.add(rule.inputId);
      }
    });

    // Include the rules that have a value, regardless of whether they have been touched or not.
    // This is necessary to ensure that the inputs with default values are included in the search.
    newViewFilters.rules.forEach((rule) => {
      if (rule.value && rule.inputId && !addedRuleIds.has(rule.inputId)) {
        eligibleRulesForSearch.push(rule);
        addedRuleIds.add(rule.inputId);
      }
    });

    form.clearErrors();
    applySearchViewFilters({ rules: eligibleRulesForSearch });
    setShouldShowResults(true);
  };

  const onReset = () => {
    form.reset({ rules: getSearchViewFilterRulesFromInputs() });
    form.clearErrors();
    resetSearchViewFilters();

    if (!view.initial_results) {
      setShouldShowResults(false);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
        {isPageEditor() ? (
          <SearchInputsSortableWrapper view={view}>
            <SearchInputs />
          </SearchInputsSortableWrapper>
        ) : (
          <SearchInputs />
        )}

        <div className="flex gap-2">
          <Button intent="primary" onClick={form.handleSubmit(onSubmit)}>
            {t('actions.apply')}
          </Button>

          <Button intent="secondary" onClick={onReset}>
            {t('actions.reset')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
